import React from "react";

import { useDragLayer } from "react-dnd";

import useMediaColorByFormat from "@/src/hooks/useMediaColorByFormat";

import MediaIcon from "./MediaIcon";
import { ITEM_HEIGHT } from "./MediaLibraryItem";
import {
  MAX_TIMELINE_Z_INDEX,
  MEDIA_LIBRARY_LIST_WIDTH,
} from "../../../constants/fast";
import { TIMESLOT } from "../../../constants/media";
import useFast from "../../../contexts/FASTContext";
import { getDurationString } from "../../../utils/data/Content";

import type { CSSProperties, JSX } from "react";
import type { DragLayerMonitor, XYCoord } from "react-dnd";

import type { MediaLibraryDragItem } from "../../../types";

function computeStyle(
  currentOffset: XYCoord | null,
  activeDropZoneId: string | null,
): CSSProperties {
  if (!currentOffset || activeDropZoneId) {
    return {
      display: "none",
    };
  }

  const { x, y } = currentOffset;

  const transform = `translate(${x}px, ${y}px)`;
  return {
    transform,
  };
}

function MediaLibraryItemDragLayer(): JSX.Element | null {
  const { isDragging, currentOffset, item } = useDragLayer<{
    item: MediaLibraryDragItem | null;
    currentOffset: XYCoord | null;
    isDragging: boolean;
  }>((monitor: DragLayerMonitor<MediaLibraryDragItem | null>) => ({
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  const { state } = useFast();
  const getMediaColor = useMediaColorByFormat();

  if (!isDragging || !item) {
    return null;
  }

  const { activeDropZoneId } = state;

  const mediaColor =
    item.type === TIMESLOT ? item?.color : getMediaColor(item?.formatKey);

  const prettyDuration = getDurationString({
    duration: item.duration / 1000,
    isShort: true,
  });

  return (
    <div
      className="pointer-events-none fixed top-0 left-0 size-full"
      style={{ zIndex: MAX_TIMELINE_Z_INDEX + 1 }}
    >
      <div
        className="opacity-90 blur-[0.5px] drop-shadow-xl"
        style={computeStyle(currentOffset, activeDropZoneId)}
      >
        <div
          className="flex flex-row rounded-[0.15625rem] pb-[0.46875rem]"
          style={{
            height: `${ITEM_HEIGHT}px`,
            width: `${MEDIA_LIBRARY_LIST_WIDTH}px`,
          }}
        >
          <div className="bg-card relative flex w-full flex-row items-center justify-between gap-[0.46875rem] rounded-r-lg py-[0.9375rem] pr-[0.78125rem] pl-[1.25rem]">
            <div
              className="absolute top-0 left-0 h-full w-[0.78125rem]"
              style={{
                backgroundColor: mediaColor,
              }}
            />
            <div className="flex gap-[0.46875rem] overflow-hidden">
              <MediaIcon mediaType={item.type} />
              <div className="flex flex-col overflow-hidden">
                <span className="truncate font-semibold">{item.label}</span>
                <span>{prettyDuration}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaLibraryItemDragLayer;
